const BASE_URL = process.env.REACT_APP_CONNECTOR_HUB_URL || '/api'

// Событие для App: сервер ответил 403 CONSENT_REQUIRED — пользователь должен
// подтвердить согласие на текущую дату документа.
export const CONSENT_REQUIRED_EVENT = 'connector-hub:consent-required'

function getToken () {
  return localStorage.getItem('ch_token')
}

// Публичные эндпоинты восстановления доступа: 401 там означает «подтверждение
// не прошло», а не «сессия истекла». Без исключения оставшийся в localStorage
// старый токен приводил бы к перезагрузке страницы прямо посреди формы
// восстановления, вместе с введёнными данными.
const PUBLIC_AUTH_PATHS = new Set([
  '/auth/password-reset/verify',
  '/auth/password-reset/confirm',
  '/auth/password-reset/redeem',
  '/auth/forgot-password',
  '/auth/email/verify',
  '/auth/email/change/confirm',
  '/auth/email/change/cancel'
])

function getHeaders () {
  const headers = { 'Content-Type': 'application/json' }
  const token = getToken()
  if (token) headers.Authorization = `Bearer ${token}`
  return headers
}

// Пустые параметры в строку запроса не попадают: сервер подставит свои
// значения по умолчанию.
function queryString (params) {
  const query = new URLSearchParams(
    Object.entries(params).filter(([, value]) => value !== null && value !== undefined && value !== '')
  ).toString()
  return query ? `?${query}` : ''
}

async function request (method, path, body) {
  const resp = await fetch(`${BASE_URL}${path}`, {
    method,
    headers: getHeaders(),
    body: body ? JSON.stringify(body) : undefined
  })

  const data = await resp.json()
  if (resp.status === 401 && getToken() && !PUBLIC_AUTH_PATHS.has(path)) {
    localStorage.removeItem('ch_token')
    window.location.reload()
    return
  }
  // Сессия без согласия на текущий документ: сервер закрыл API. Приложение
  // узнаёт об этом событием и само ставит экран согласия на место страницы.
  // Перезагружать страницу нельзя: предзагрузка галереи идёт параллельно с
  // /auth/me и получает 403 первой — перезагрузка зацикливалась, и галочку
  // было не поставить (стенд 2026-09-24). Сессия остаётся: экран согласия
  // выдаст новую после «Продолжить». Сам /auth/consent событие не шлёт,
  // иначе отказ на нём переводил бы экран в самого себя.
  if (resp.status === 403 && data.error?.code === 'CONSENT_REQUIRED' && path !== '/auth/consent') {
    window.dispatchEvent(new Event(CONSENT_REQUIRED_EVENT))
  }
  if (!resp.ok) {
    const error = new Error(data.error?.message || data.error || `HTTP ${resp.status}`)
    error.code = data.error?.code
    error.details = data.error?.details
    error.status = resp.status
    error.portal = data.portal || null
    throw error
  }
  return data
}

export const api = {
  // Auth
  register: (email, password, fintabloToken, personalDataAccepted) =>
    request('POST', '/auth/register', {
      email,
      password,
      fintablo_token: fintabloToken,
      personal_data_accepted: personalDataAccepted === true
    }),
  acceptConsent: () =>
    request('POST', '/auth/consent', { personal_data_accepted: true }),
  login: (email, password) =>
    request('POST', '/auth/login', { email, password }),
  forgotPassword: (email) =>
    request('POST', '/auth/forgot-password', { email }),
  redeemPasswordReset: (token) =>
    request('POST', '/auth/password-reset/redeem', { token }),
  verifyEmail: (token) =>
    request('POST', '/auth/email/verify', { token }),
  changeEmail: (currentPassword, email) =>
    request('PATCH', '/auth/email', { current_password: currentPassword, email }),
  confirmEmailChange: (token) =>
    request('POST', '/auth/email/change/confirm', { token }),
  cancelEmailChange: (token) =>
    request('POST', '/auth/email/change/cancel', { token }),
  resendEmailVerification: () =>
    request('POST', '/auth/email/verify/resend'),
  verifyPasswordReset: (email, fintabloToken) =>
    request('POST', '/auth/password-reset/verify', { email, fintablo_token: fintabloToken }),
  confirmPasswordReset: (resetToken, password) =>
    request('POST', '/auth/password-reset/confirm', { reset_token: resetToken, password }),
  changePassword: (currentPassword, newPassword) =>
    request('PATCH', '/auth/password', { current_password: currentPassword, new_password: newPassword }),
  me: () => request('GET', '/auth/me'),

  // OAuth amoCRM
  getOauthUrl: () => request('GET', '/oauth/amocrm/start'),

  // OAuth Bitrix24
  getBitrix24OauthUrl: () => request('GET', '/oauth/bitrix24/start'),
  claimBitrix24Connection: (claimToken) =>
    request('POST', '/oauth/bitrix24/claim', { claim_token: claimToken }),

  // Явное подтверждение смены портала: гасит активный и привязывает новый
  // одним запросом —два отдельных оставили бы пользователя без подключения.
  replaceBitrix24Portal: (claimToken) =>
    request('POST', '/oauth/bitrix24/claim', { claim_token: claimToken, replace_active: true }),

  // Коннекторы
  getConnectors: () => request('GET', '/connectors'),
  getConnections: () => request('GET', '/connectors/connections'),
  getScenarioFields: (slug) => request('GET', `/connectors/${slug}/scenario-fields`),

  // UI contract v1 (IT5.2)
  getConnectorHub: (slug) => request('GET', `/ui/v1/connectors/${slug}/hub`),
  bootstrapScenario: (slug) => request('POST', `/ui/v1/connectors/${slug}/scenarios/bootstrap`),
  createScenario: (slug) => request('POST', `/ui/v1/connectors/${slug}/scenarios`),
  getScenarioEditor: (scenarioId) => request('GET', `/ui/v1/scenarios/${scenarioId}/editor`),
  getReferenceEditor: (connectionId, type) =>
    request('GET', `/ui/v1/connections/${connectionId}/references/${type}`),
  getIntegrationEvents: (params = {}) => {
    const query = new URLSearchParams(Object.entries(params).filter(([, value]) => value != null)).toString()
    return request('GET', `/ui/v1/events${query ? `?${query}` : ''}`)
  },
  validateScenario: (connectorSlug, scenario) =>
    request('POST', '/ui/v1/scenarios/validate', { connector_slug: connectorSlug, scenario }),
  saveScenario: (scenarioId, scenario, expectedUpdatedAt) =>
    request('PUT', `/ui/v1/scenarios/${scenarioId}`, { scenario, expected_updated_at: expectedUpdatedAt }),
  startScenario: (scenarioId, payload) => request('POST', `/ui/v1/scenarios/${scenarioId}/start`, payload),
  pauseScenario: (scenarioId) => request('POST', `/ui/v1/scenarios/${scenarioId}/pause`),
  duplicateScenario: (scenarioId) => request('POST', `/ui/v1/scenarios/${scenarioId}/duplicate`),
  deleteScenario: (scenarioId) => request('DELETE', `/ui/v1/scenarios/${scenarioId}`),
  getLiveFields: (slug) => request('GET', `/connectors/${slug}/live-fields`),

  // Выгрузка прошлых периодов (history-export, ADR R20)
  listExports: (slug) => request('GET', `/ui/v1/connectors/${slug}/exports`),
  createExport: (slug) => request('POST', `/ui/v1/connectors/${slug}/exports`),
  getExport: (exportId) => request('GET', `/ui/v1/exports/${exportId}`),
  saveExport: (exportId, settings) => request('PUT', `/ui/v1/exports/${exportId}`, { settings }),
  validateExport: (exportId, settings) => request('POST', `/ui/v1/exports/${exportId}/validate`, { settings }),
  prepareExport: (exportId) => request('POST', `/ui/v1/exports/${exportId}/prepare`),
  trialExport: (exportId) => request('POST', `/ui/v1/exports/${exportId}/trial`),
  startExport: (exportId) => request('POST', `/ui/v1/exports/${exportId}/start`),
  stopExport: (exportId) => request('POST', `/ui/v1/exports/${exportId}/stop`),
  saveExportTracking: (exportId, tracking) => request('PUT', `/ui/v1/exports/${exportId}/tracking`, tracking),
  deleteExport: (exportId) => request('DELETE', `/ui/v1/exports/${exportId}`),
  continueExport: (exportId) => request('POST', `/ui/v1/exports/${exportId}/continue`),
  // Значения условия, которых нет в самом поле: грузятся по выбору поля, а не
  // при открытии редактора.
  getFieldOptions: (slug, field) => request('GET', `/connectors/${slug}/field-options?field=${encodeURIComponent(field)}`),

  // Пользователь
  syncPipelines: (id) => request('POST', `/users/${id}/pipelines/sync`),
  getPipelines: (id) => request('GET', `/users/${id}/pipelines`),
  getFintabloStatuses: (id) => request('GET', `/users/${id}/fintablo-statuses`),
  getFintabloToken: (id) => request('GET', `/users/${id}/fintablo-token`),
  saveFintabloToken: (id, token) => request('PATCH', `/users/${id}/fintablo-token`, { fintablo_token: token }),

  // Bitrix24
  getBitrix24Pipelines: (id) => request('GET', `/users/${id}/bitrix24-pipelines`),
  disconnectConnection: (connectionId) => request('DELETE', `/connections/${connectionId}`),
  // Повтор регистрации вебхука после отказа CRM: токены сохранены, повторная
  // авторизация не требуется.
  retryWebhookRegistration: (connectionId) => request('POST', `/connections/${connectionId}/retry-webhook-registration`),

  // Employees
  syncEmployees: (userId) =>
    request('POST', `/employees/${userId}/sync`),
  getEmployeeMappings: (userId) =>
    request('GET', `/employees/${userId}/mappings`),
  getFintabloEmployees: (userId) =>
    request('GET', `/employees/${userId}/fintablo-employees`),
  saveEmployeeMapping: (userId, amocrmUserId, fintabloPartnerId) =>
    request('POST', `/employees/${userId}/mappings`, { amocrm_user_id: amocrmUserId, fintablo_partner_id: fintabloPartnerId }),
  deleteEmployeeMapping: (userId, amocrmUserId) =>
    request('DELETE', `/employees/${userId}/mappings/${amocrmUserId}`),

  // Bitrix24 Employees
  syncBitrix24Employees: (connectionId) =>
    request('POST', `/bitrix24-employees/${connectionId}/sync`),
  getBitrix24EmployeeMappings: (connectionId) =>
    request('GET', `/bitrix24-employees/${connectionId}/mappings`),
  getBitrix24FintabloEmployees: (connectionId) =>
    request('GET', `/bitrix24-employees/${connectionId}/fintablo-employees`),
  saveBitrix24EmployeeMappingsBatch: (connectionId, mappings) =>
    request('POST', `/bitrix24-employees/${connectionId}/mappings/batch`, { mappings }),
  deleteBitrix24EmployeeMapping: (connectionId, b24UserId) =>
    request('DELETE', `/bitrix24-employees/${connectionId}/mappings/${b24UserId}`),

  // Bitrix24 Counterparties (IT3 task 7; страницы и поиск — counterparty-large-accounts)
  syncBitrix24Counterparties: (connectionId, mode = null) =>
    request('POST', `/bitrix24-counterparties/${connectionId}/sync`, mode ? { mode } : undefined),
  getBitrix24CounterpartyMappings: (connectionId, params = {}) =>
    request('GET', `/bitrix24-counterparties/${connectionId}/mappings${queryString(params)}`),
  searchBitrix24CounterpartiesInCrm: (connectionId, q) =>
    request('GET', `/bitrix24-counterparties/${connectionId}/crm-search${queryString({ q })}`),
  addBitrix24CounterpartiesFromCrm: (connectionId, items) =>
    request('POST', `/bitrix24-counterparties/${connectionId}/crm-add`, { items }),
  markBitrix24CounterpartiesReviewed: (connectionId, seenAt) =>
    request('POST', `/bitrix24-counterparties/${connectionId}/mappings/reviewed`, { seen_at: seenAt }),
  saveBitrix24CounterpartyMappingsBatch: (connectionId, mappings) =>
    request('POST', `/bitrix24-counterparties/${connectionId}/mappings/batch`, { mappings }),
  deleteBitrix24CounterpartyMapping: (connectionId, entityType, entityId) =>
    request('DELETE', `/bitrix24-counterparties/${connectionId}/mappings/${entityType}/${entityId}`),
  getBitrix24FintabloPartners: (connectionId) =>
    request('GET', `/bitrix24-counterparties/${connectionId}/fintablo-partners`),

  // Bitrix24 Directions (direction-by-pipeline)
  syncBitrix24Directions: (connectionId) =>
    request('POST', `/bitrix24-directions/${connectionId}/sync`),
  getBitrix24DirectionMappings: (connectionId, params = {}) =>
    request('GET', `/bitrix24-directions/${connectionId}/mappings${queryString(params)}`),
  getBitrix24FintabloDirections: (connectionId) =>
    request('GET', `/bitrix24-directions/${connectionId}/fintablo-directions`),
  saveBitrix24DirectionMappingsBatch: (connectionId, mappings) =>
    request('POST', `/bitrix24-directions/${connectionId}/mappings/batch`, { mappings }),
  deleteBitrix24DirectionMapping: (connectionId, fieldKey, valueId) =>
    request('DELETE', `/bitrix24-directions/${connectionId}/mappings/${encodeURIComponent(fieldKey)}/${encodeURIComponent(valueId)}`),
  markBitrix24DirectionsReviewed: (connectionId, seenAt) =>
    request('POST', `/bitrix24-directions/${connectionId}/mappings/reviewed`, { seen_at: seenAt }),
  setBitrix24DirectionSettings: (connectionId, multiSelect) =>
    request('PUT', `/bitrix24-directions/${connectionId}/settings`, { multi_select: multiSelect }),

  // amoCRM Directions (amocrm-direction-reference)
  syncAmocrmDirections: (connectionId) =>
    request('POST', `/amocrm-directions/${connectionId}/sync`),
  getAmocrmDirectionMappings: (connectionId, params = {}) =>
    request('GET', `/amocrm-directions/${connectionId}/mappings${queryString(params)}`),
  getAmocrmFintabloDirections: (connectionId) =>
    request('GET', `/amocrm-directions/${connectionId}/fintablo-directions`),
  saveAmocrmDirectionMappingsBatch: (connectionId, mappings) =>
    request('POST', `/amocrm-directions/${connectionId}/mappings/batch`, { mappings }),
  deleteAmocrmDirectionMapping: (connectionId, fieldKey, valueId) =>
    request('DELETE', `/amocrm-directions/${connectionId}/mappings/${encodeURIComponent(fieldKey)}/${encodeURIComponent(valueId)}`),
  markAmocrmDirectionsReviewed: (connectionId, seenAt) =>
    request('POST', `/amocrm-directions/${connectionId}/mappings/reviewed`, { seen_at: seenAt }),
  setAmocrmDirectionSettings: (connectionId, multiSelect) =>
    request('PUT', `/amocrm-directions/${connectionId}/settings`, { multi_select: multiSelect }),

  // amoCRM Counterparties (amocrm-counterparty-reference; страницы и поиск — counterparty-large-accounts)
  syncAmocrmCounterparties: (connectionId, mode = null) =>
    request('POST', `/amocrm-counterparties/${connectionId}/sync`, mode ? { mode } : undefined),
  getAmocrmCounterpartyMappings: (connectionId, params = {}) =>
    request('GET', `/amocrm-counterparties/${connectionId}/mappings${queryString(params)}`),
  searchAmocrmCounterpartiesInCrm: (connectionId, q) =>
    request('GET', `/amocrm-counterparties/${connectionId}/crm-search${queryString({ q })}`),
  addAmocrmCounterpartiesFromCrm: (connectionId, items) =>
    request('POST', `/amocrm-counterparties/${connectionId}/crm-add`, { items }),
  markAmocrmCounterpartiesReviewed: (connectionId, seenAt) =>
    request('POST', `/amocrm-counterparties/${connectionId}/mappings/reviewed`, { seen_at: seenAt }),
  saveAmocrmCounterpartyMappingsBatch: (connectionId, mappings) =>
    request('POST', `/amocrm-counterparties/${connectionId}/mappings/batch`, { mappings }),
  deleteAmocrmCounterpartyMapping: (connectionId, entityType, entityId) =>
    request('DELETE', `/amocrm-counterparties/${connectionId}/mappings/${entityType}/${entityId}`),
  getAmocrmFintabloPartners: (connectionId) =>
    request('GET', `/amocrm-counterparties/${connectionId}/fintablo-partners`)
}
